import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { usePending } from "../hooks/usePending";
import { useToday } from "../hooks/useToday";
import { useNav } from "../hooks/useNav";
import useIsMobile from "../hooks/useIsMobile";
import { AREAS, areaBadge, locate } from "./navModel";
import Glyph from "./navGlyphs";
import NewLeadModal from "./NewLeadModal";

// The phone frame: the sidebar becomes a bottom tab bar, and creation becomes a
// floating "+".
//
// Today, Sales, Meetings, More — the mockup's four (Navigation.dc.html, screen
// 03). Today is a link. An area tab opens its children as a sheet, the way More
// always has; More holds the account (which opens Settings), Inbound and
// Reports, each still in its group. The areas and their pages come from
// navModel, the same file the sidebar reads.
const TAB_AREAS = ["sales", "meetings"];
const MORE_AREAS = ["inbound", "reports"];
const areaById = (id) => AREAS.find((a) => a.id === id);

// The two triage screens are the ones a lead gets typed into from — everywhere
// else either has its own primary action (Leads) or is a record, not a list.
const FAB_PATHS = ["/", "/today"];

function Sheet({ label, onClose, children }) {
  // Escape closes, and the page behind must not scroll under an open sheet.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <>
      <div className="crm-sheet__backdrop" onClick={onClose} />
      <div className="crm-sheet" role="dialog" aria-modal="true" aria-label={label}>
        <div className="crm-sheet__grip" aria-hidden="true" />
        {children}
      </div>
    </>
  );
}

function TabBadge({ n }) {
  return n > 0 ? <span className="crm-tab__badge crm-num">{n}</span> : null;
}

// One area's pages in a sheet. `big` is the area's own sheet (Sales, Meetings),
// where the heading is the title; inside More the headings are small group labels.
function AreaGroup({ area, badges, here, big }) {
  return (
    <div className="crm-sheet__group">
      <div className={`crm-sheet__grouphead${big ? " is-big" : ""}`}>
        <Glyph name={area.glyph} size={16} />
        <span>{area.label}</span>
      </div>
      {area.items.map((item) =>
        item.soon ? (
          <span key={item.key} className="crm-sheet__item crm-sheet__item--nested is-soon" aria-disabled="true">
            <span className="crm-sheet__itemlabel">{item.label}</span>
            <span className="crm-side__soon">Soon</span>
          </span>
        ) : (
        <Link
          key={item.key}
          to={item.to}
          className={`crm-sheet__item crm-sheet__item--nested${here.area === area.id && here.item === item.key ? " active" : ""}`}
        >
          <span className="crm-sheet__itemlabel">{item.label}</span>
          {item.badge && badges[item.badge] > 0 && (
            <span className="crm-side__badge crm-num">{badges[item.badge]}</span>
          )}
          <Glyph name="chevronRight" size={16} className="crm-sheet__chev" />
        </Link>
        )
      )}
    </div>
  );
}

export default function MobileNav() {
  const isMobile = useIsMobile();
  const { count: pending } = usePending();
  const { total: today } = useToday();
  const { followUps, hint } = useNav();
  const [sheet, setSheet] = useState(null);
  const [adding, setAdding] = useState(false);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  // Route changes close the sheet — otherwise tapping a destination leaves the
  // sheet sitting over the page it just opened.
  useEffect(() => { setSheet(null); }, [pathname, search]);

  if (!isMobile) return null;

  const here = locate(pathname, search, hint);
  const badges = { pending, followups: followUps };
  const onMore = [...MORE_AREAS, "settings"].includes(here.area);
  const toggle = (id) => setSheet((s) => (s === id ? null : id));

  return (
    <>
      {FAB_PATHS.includes(pathname) && (
        <button
          type="button"
          className="crm-fab"
          aria-label="New lead"
          onClick={() => setAdding(true)}
        >
          +
        </button>
      )}

      <nav className="crm-tabbar" aria-label="Main">
        <Link to="/today" className={`crm-tab${here.area === "today" && !sheet ? " active" : ""}`}>
          <span className="crm-tab__ico">
            <Glyph name="sun" size={20} />
            <TabBadge n={today} />
          </span>
          <span className="crm-tab__label">Today</span>
        </Link>
        {TAB_AREAS.map((id) => {
          const area = areaById(id);
          const active = sheet ? sheet === id : here.area === id;
          return (
            <button
              key={id}
              type="button"
              className={`crm-tab${active ? " active" : ""}`}
              aria-expanded={sheet === id}
              onClick={() => toggle(id)}
            >
              <span className="crm-tab__ico">
                <Glyph name={area.glyph} size={20} />
                <TabBadge n={areaBadge(area, badges)} />
              </span>
              <span className="crm-tab__label">{area.label}</span>
            </button>
          );
        })}
        <button
          type="button"
          className={`crm-tab${(sheet ? sheet === "more" : onMore) ? " active" : ""}`}
          aria-expanded={sheet === "more"}
          onClick={() => toggle("more")}
        >
          <span className="crm-tab__ico"><Glyph name="more" size={20} /></span>
          <span className="crm-tab__label">More</span>
        </button>
      </nav>

      {sheet && sheet !== "more" && (
        <Sheet label={areaById(sheet).label} onClose={() => setSheet(null)}>
          <AreaGroup area={areaById(sheet)} badges={badges} here={here} big />
        </Sheet>
      )}

      {sheet === "more" && (
        <Sheet label="More" onClose={() => setSheet(null)}>
          <Link to="/settings" className={`crm-sheet__me${here.area === "settings" ? " active" : ""}`}>
            <span className="crm-side__avatar">GE</span>
            <span className="crm-side__who">
              <b>Gabriel</b>
              <span>Founder · Settings</span>
            </span>
            <Glyph name="settings" size={16} className="crm-sheet__chev" />
          </Link>
          {MORE_AREAS.map((id) => (
            <AreaGroup key={id} area={areaById(id)} badges={badges} here={here} />
          ))}
        </Sheet>
      )}

      {adding && (
        <NewLeadModal
          onClose={() => setAdding(false)}
          onSaved={(contact) => {
            setAdding(false);
            // Land on the record just created rather than back on the list that
            // has no idea it exists — the page underneath the FAB never reloaded.
            if (contact?.id) {
              navigate(`/contacts/${contact.id}`, { state: { from: { to: pathname, label: pathname === "/today" ? "Today" : "Pipeline" } } });
            } else {
              navigate("/leads");
            }
          }}
        />
      )}
    </>
  );
}
